/**
 * Desfechos de luta aceitos pela fight-api-v2.
 *
 * O `value` é exatamente o membro do enum `FinishTypes`
 * (fight-api-v2/src/constants/finish-types.enum.ts), porque é ele que vai no
 * `result` do `PATCH /fights/:id` — que valida com `@IsEnum(FinishTypes)` e
 * responde 400 para qualquer coisa fora da lista. O `label` é só o texto do
 * Select e o que fica em `extraFields.finishType`, lido pelo mike-goldberg e
 * pelos widgets.
 */
export interface FinishTypeOption {
  value: string;
  label: string;
}

const option = (value: string, label: string): FinishTypeOption => ({
  value,
  label,
});

const TKO = option('TKO', 'TKO');
const KO = option('KO', 'KO');
const SUBMISSION = option('SUBMISSION', 'Submission');
const SUBMISSION_ATTP = option('SUBMISSION_ATTP', 'Submission Attp');
const UNANIMOUS_DECISION = option('UNANIMOUS_DECISION', 'Unanimous Decision');
const MAJORITY_DECISION = option('MAJORITY_DECISION', 'Majority Decision');
const SPLIT_DECISION = option('SPLIT_DECISION', 'Split Decision');
const DRAW = option('DRAW', 'Draw');
const MAJORITY_DRAW = option('MAJORITY_DRAW', 'Majority Draw');
const NO_CONTEST = option('NO_CONTEST', 'No Contest');
const DQ = option('DQ', 'Disqualification');
const POINTS = option('POINTS', 'Points');
const PIN_FALL = option('PIN_FALL', 'Pin/Fall');
const TECHNICAL_FALL = option('TECHNICAL_FALL', 'Technical Fall');
const TECHNICAL_SUPERIORITY = option(
  'TECHNICAL_SUPERIORITY',
  'Technical Superiority',
);
const DEFAULT = option('DEFAULT', 'Default');
const FORFEIT = option('FORFEIT', 'Forfeit');
const INJURY = option('INJURY', 'Injury');

export const MMA_FINISH_TYPES: FinishTypeOption[] = [
  TKO,
  KO,
  SUBMISSION,
  UNANIMOUS_DECISION,
  MAJORITY_DECISION,
  SPLIT_DECISION,
  DRAW,
  NO_CONTEST,
  MAJORITY_DRAW,
  DQ,
];

/**
 * Boxe, kickboxing e muay thai: sem finalização — era esse o defeito de usar a
 * lista de MMA no boxe. Desistência de corner (RTD) e parada por corte são
 * gravadas como `TKO`/`INJURY`, que é como o enum representa isso hoje.
 */
export const STRIKING_FINISH_TYPES: FinishTypeOption[] = [
  TKO,
  KO,
  UNANIMOUS_DECISION,
  SPLIT_DECISION,
  MAJORITY_DECISION,
  POINTS,
  DRAW,
  MAJORITY_DRAW,
  NO_CONTEST,
  DQ,
  FORFEIT,
  INJURY,
];

export const WRESTLING_FINISH_TYPES: FinishTypeOption[] = [
  PIN_FALL,
  POINTS,
  TECHNICAL_FALL,
  MAJORITY_DECISION,
  SUBMISSION,
  SUBMISSION_ATTP,
  DEFAULT,
  FORFEIT,
  DQ,
  TECHNICAL_SUPERIORITY,
  INJURY,
];

const FINISH_TYPES_BY_SPORT: Record<string, FinishTypeOption[]> = {
  MMA: MMA_FINISH_TYPES,
  BOXING: STRIKING_FINISH_TYPES,
  KICKBOXING: STRIKING_FINISH_TYPES,
  MUAY_THAI: STRIKING_FINISH_TYPES,
  WRESTLING: WRESTLING_FINISH_TYPES,
};

/** Lista de opções do esporte. Esporte desconhecido cai no MMA. */
export function getFinishTypesForSport(sport?: string): FinishTypeOption[] {
  if (!sport) return MMA_FINISH_TYPES;
  return FINISH_TYPES_BY_SPORT[sport] || MMA_FINISH_TYPES;
}

export const ALL_FINISH_TYPES: FinishTypeOption[] = [
  TKO,
  KO,
  SUBMISSION,
  SUBMISSION_ATTP,
  UNANIMOUS_DECISION,
  MAJORITY_DECISION,
  SPLIT_DECISION,
  DRAW,
  MAJORITY_DRAW,
  NO_CONTEST,
  DQ,
  POINTS,
  PIN_FALL,
  TECHNICAL_FALL,
  TECHNICAL_SUPERIORITY,
  DEFAULT,
  FORFEIT,
  INJURY,
];

/**
 * Normaliza para o valor do enum. Aceita tanto o valor canônico quanto o texto
 * legível que as actions antigas gravaram em `extraFields.finishType`
 * ("Unanimous Decision", "Disqualification", "Pin/Fall", ...).
 */
export function toFinishTypeValue(finish?: string | null): string | undefined {
  if (!finish) return undefined;

  const normalized = finish.trim().toUpperCase().replaceAll(' ', '_');
  const byValue = ALL_FINISH_TYPES.find((item) => item.value === normalized);
  if (byValue) return byValue.value;

  const byLabel = ALL_FINISH_TYPES.find(
    (item) => item.label.toUpperCase() === finish.trim().toUpperCase(),
  );
  if (byLabel) return byLabel.value;

  // Textos legados sem correspondência direta no enum.
  if (normalized === 'DISQUALIFICATION') return DQ.value;
  if (normalized === 'PIN/FALL') return PIN_FALL.value;

  return undefined;
}

/** Texto legível de um desfecho; devolve o próprio valor se for desconhecido. */
export function getFinishTypeLabel(finish?: string | null): string | undefined {
  if (!finish) return undefined;

  const value = toFinishTypeValue(finish);
  const found = ALL_FINISH_TYPES.find((item) => item.value === value);
  return found ? found.label : finish;
}

/** Opção pronta para o Select a partir do que ficou gravado na action. */
export function toFinishTypeOption(
  finish?: string | null,
): FinishTypeOption | null {
  const value = toFinishTypeValue(finish);
  if (!value) return null;

  return { value, label: getFinishTypeLabel(value) as string };
}

/** Categoria usada para incrementar cartel e cartel do lutador. */
export function getFinishCategory(
  finish?: string | null,
): 'kotko' | 'submission' | 'decision' | null {
  const value = toFinishTypeValue(finish);
  if (!value) return null;

  if (value === KO.value || value === TKO.value) return 'kotko';
  if (value.startsWith('SUBMISSION')) return 'submission';
  if (value.includes('DECISION')) return 'decision';
  return null;
}
